import { ChevronLeft, ChevronRight, Close } from '@mui/icons-material';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import { memo, MouseEvent, ReactElement, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface ImageZoomCardProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  currentIndex?: number;
  totalCount?: number;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  initialScale?: number;
}

export const ImageZoomCard = memo(function FullScreenModal({
  isOpen,
  onClose,
  children,
  currentIndex,
  totalCount,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
  initialScale = 2.0,
}: ImageZoomCardProps): ReactElement {
  const [currentScale, setCurrentScale] = useState(initialScale);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [lastClickTime, setLastClickTime] = useState(0);
  const zoomNodeRef = useRef<HTMLDivElement | null>(null);

  // Determine if carousel features should be shown
  const showCarousel = useMemo(() => {
    return totalCount && totalCount > 1 && onPrevious && onNext;
  }, [totalCount, onPrevious, onNext]);

  // Handle mouse wheel zoom
  const handleWheel = useCallback((event: WheelEvent) => {
    event.preventDefault();
    const zoomIntensity = 0.4;
    const direction = event.deltaY > 0 ? -1 : 1;
    setCurrentScale((prevScale) => Math.min(Math.max(prevScale + direction * zoomIntensity, 1.0), 6.0));
  }, []);

  // Reset pan when zooming out to initial scale
  useEffect(() => {
    if (currentScale === initialScale) {
      setPanOffset({ x: 0, y: 0 });
    }
  }, [currentScale, initialScale]);

  const zoomContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (zoomNodeRef.current) {
        zoomNodeRef.current.removeEventListener('wheel', handleWheel);
      }
      zoomNodeRef.current = node;
      if (node) {
        node.addEventListener('wheel', handleWheel, { passive: false });
      }
    },
    [handleWheel]
  );

  // Handle mouse down for panning
  const handleMouseDown = useCallback(
    (event: MouseEvent) => {
      if (currentScale > initialScale) {
        setIsDragging(true);
        setDragStart({
          x: event.clientX - panOffset.x,
          y: event.clientY - panOffset.y,
        });
      }
      setLastClickTime(Date.now());
    },
    [currentScale, panOffset, initialScale]
  );

  // Handle mouse move for panning
  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (isDragging && currentScale > initialScale) {
        const newOffset = {
          x: event.clientX - dragStart.x,
          y: event.clientY - dragStart.y,
        };
        setPanOffset(newOffset);
      }
    },
    [isDragging, dragStart, currentScale, initialScale]
  );

  // Handle mouse up to stop panning
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Handle click to zoom toggle
  const handleClick = useCallback(() => {
    const clickDuration = Date.now() - lastClickTime;
    // 200ms
    if (clickDuration < 200 && !isDragging) {
      if (currentScale >= 4.0) {
        setCurrentScale(initialScale);
        setPanOffset({ x: 0, y: 0 });
      } else {
        setCurrentScale(4.0);
        setPanOffset({ x: 0, y: 0 });
      }
    }
  }, [lastClickTime, isDragging, currentScale, initialScale]);

  useEffect(() => {
    if (isOpen) {
      setCurrentScale(initialScale);
      setPanOffset({ x: 0, y: 0 });
      setIsDragging(false);
    }
  }, [isOpen, currentIndex, initialScale]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      fullScreen
      PaperProps={{
        sx: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'transparent',
          overflow: 'hidden',
        },
      }}
      aria-labelledby={'carousel-modal-title'}
    >
      <Box
        sx={{
          height: '85vh',
          width: '75vw',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: colors.modalBackground,
          borderRadius: 4,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            ...buttonSx,
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 2,
          }}
          aria-label='Close modal'
        >
          <Close />
        </IconButton>
        {/* Main content area */}
        <Box
          ref={zoomContainerRef}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden',
            height: '90%',
            width: '90%',
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {showCarousel && (
            <IconButton
              onClick={onPrevious}
              disabled={!(hasPrevious ?? false)}
              sx={{
                ...buttonSx,
                position: 'absolute',
                left: 16,
                zIndex: 2,
              }}
              aria-label='Previous image'
            >
              <ChevronLeft />
            </IconButton>
          )}{' '}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: currentScale > 1.0 ? (isDragging ? 'grabbing' : 'grab') : 'pointer',
            }}
            onClick={handleClick}
          >
            <Box
              sx={{
                transform: `translate3d(${panOffset.x}px, ${panOffset.y}px, 0) scale(${currentScale})`,
                transformOrigin: 'center',
                transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                userSelect: 'none',
                pointerEvents: 'auto',
                minWidth: 0,
                minHeight: 0,
              }}
            >
              {children}
            </Box>
          </Box>
          {showCarousel && (
            <IconButton
              onClick={onNext}
              disabled={!(hasNext ?? false)}
              size='medium'
              sx={{
                ...buttonSx,
                position: 'absolute',
                right: 16,
                zIndex: 2,
              }}
              aria-label='Next image'
            >
              <ChevronRight fontSize='medium' />
            </IconButton>
          )}
          {showCarousel && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 2,
                padding: '8px 16px',
                borderRadius: 2,
                backgroundColor: colors.buttonBackground,
              }}
            >
              <Typography variant='h6' sx={{ color: colors.counterText }}>
                {(currentIndex ?? 0) + 1} / {totalCount}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
});

const colors = {
  modalBackground: 'rgba(255, 255, 255, 1)',
  buttonText: 'black',
  buttonBackground: 'rgba(190, 190, 190, 0.5)',
  buttonHoverBackground: 'rgba(83, 83, 83, 1)',
  buttonHoverText: 'white',
  buttonDisabledText: 'rgba(145, 145, 145, 0.3)',
  counterText: 'black',
};

const buttonSx = {
  color: colors.buttonText,
  backgroundColor: colors.buttonBackground,
  '&:hover': {
    backgroundColor: colors.buttonHoverBackground,
    color: colors.buttonHoverText,
  },
  '&:disabled': {
    color: colors.buttonDisabledText,
  },
};
